import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Stack, TextField } from "@mui/material";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DesktopDatePicker from "@mui/lab/DesktopDatePicker";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Data
// import salesByAuthor from "layouts/dashboard/components/Sales/data/salesByAuthor";
import SalesByCourse from "layouts/dashboard/components/Sales/data/salesByCourse";
import SalesByLesson from "layouts/dashboard/components/Sales/data/salesByLesson";

function formatDate(date) {
  const today = date.split("-");
  const dd = today[0];

  const mm = parseInt(today[1], 10) - 1;
  const yyyy = today[2];

  return new Date(yyyy, mm, dd);
}

function Sales() {
  const courses = useSelector((state) => state.course.allCourses);
  const lessons = useSelector((state) => state.lesson.allLessons);
  const [menu, setMenu] = useState(null);
  const [data, setData] = useState(["course", "cours"]);
  const [courseCount, setCourseCount] = useState([]);
  const [lessonCount, setLessonCount] = useState([]);
  const [fields, setFields] = useState({
    dateDebut: { value: null },
    dateFin: { value: null },
  });

  const openMenu = ({ currentTarget }) => setMenu(currentTarget);
  const closeMenu = (_data) => {
    setMenu(null);
    setData(_data);
  };

  const resetSearch = () => {
    setFields({
      dateDebut: { value: null },
      dateFin: { value: null },
    });
    setCourseCount(courses.reduce((total, course) => total + course.subscriptions.length, 0));
    setLessonCount(lessons.reduce((total, lesson) => total + lesson.subscriptions.length, 0));
  };

  const filterData = (dateDebut, dateFin, subscriptions) => {
    let currentData = subscriptions;
    if (dateDebut !== null)
      currentData = currentData.filter((s) => dateDebut <= formatDate(s.createdAt));
    if (dateFin !== null)
      currentData = currentData.filter((s) => formatDate(s.createdAt) <= dateFin);

    return currentData.length;
  };

  useEffect(() => {
    setCourseCount(courses.reduce((total, course) => total + course.subscriptions.length, 0));
  }, [courses]);

  useEffect(() => {
    setLessonCount(lessons.reduce((total, lesson) => total + lesson.subscriptions.length, 0));
  }, [lessons]);

  const handleChangeSearch = (name, value) => {
    setFields((f) => ({
      ...f,
      [name]: { value },
    }));
    if (data[0] === "course") {
      setCourseCount(
        courses.reduce(
          (total, course) =>
            total +
            filterData(
              name === "dateDebut" ? value : fields.dateDebut.value,
              name === "dateFin" ? value : fields.dateFin.value,
              course.subscriptions
            ),
          0
        )
      );
    }
    if (data[0] === "lesson") {
      setLessonCount(
        lessons.reduce(
          (total, lesson) =>
            total +
            filterData(
              name === "dateDebut" ? value : fields.dateDebut.value,
              name === "dateFin" ? value : fields.dateFin.value,
              lesson.subscriptions
            ),
          0
        )
      );
    }

    if (
      (name === "dateDebut" ? value : fields.dateDebut.value) === null &&
      (name === "dateFin" ? value : fields.dateFin.value) === null
    ) {
      setCourseCount(courses.reduce((total, course) => total + course.subscriptions.length, 0));
      setLessonCount(lessons.reduce((total, lesson) => total + lesson.subscriptions.length, 0));
    }
  };

  const renderMenu = (
    <Menu
      id="simple-menu"
      anchorEl={menu}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={Boolean(menu)}
      onClose={() => closeMenu(["course", "cours"])}
    >
      <MenuItem onClick={() => closeMenu(["course", "cours"])}>Cours</MenuItem>
      <MenuItem onClick={() => closeMenu(["lesson", "léçons"])}>Lessons</MenuItem>
      {/* <MenuItem onClick={() => closeMenu(["author", "auteurs"])}>Auteurs</MenuItem> */}
    </Menu>
  );

  return (
    <Card>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
        <MDBox>
          <MDTypography variant="h6" gutterBottom>
            Ventes
          </MDTypography>
          <MDBox display="flex" alignItems="center" lineHeight={0}>
            <Icon
              sx={{
                fontWeight: "bold",
                color: ({ palette: { info } }) => info.main,
                mt: -0.5,
              }}
            >
              done
            </Icon>
            <MDTypography variant="button" fontWeight="regular" color="text">
              &nbsp;<strong>{data[0] === "course" ? courseCount : lessonCount} ventes</strong> des{" "}
              {data[1]}
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox color="text" px={2}>
          <Icon sx={{ cursor: "pointer", fontWeight: "bold" }} fontSize="small" onClick={openMenu}>
            more_vert
          </Icon>
        </MDBox>
        {renderMenu}
      </MDBox>
      <MDBox
        sx={{
          p: 1,
          m: 1,
        }}
      >
        <Stack justifyContent="right" direction={{ xs: "column", md: "row" }} spacing={1}>
          <div>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack spacing={1} sx={{ width: "100%", ml: 1, my: 1 }} direction="row">
                <DesktopDatePicker
                  label="Date début"
                  inputFormat="dd/MM/yyyy"
                  value={fields.dateDebut.value}
                  onChange={(e) => handleChangeSearch("dateDebut", e)}
                  renderInput={(params) => <TextField style={{ width: "inherit" }} {...params} />}
                />
                <DesktopDatePicker
                  label="Date fin"
                  inputFormat="dd/MM/yyyy"
                  value={fields.dateFin.value}
                  onChange={(e) => handleChangeSearch("dateFin", e)}
                  renderInput={(params) => <TextField style={{ width: "inherit" }} {...params} />}
                />
              </Stack>
            </LocalizationProvider>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center" }}>
            <RestartAltIcon sx={{ m: 1 }} onClick={() => resetSearch()} />
          </div>
        </Stack>
      </MDBox>
      {data[0] === "course" && (
        <SalesByCourse dateDebut={fields.dateDebut.value} dateFin={fields.dateFin.value} />
      )}
      {data[0] === "lesson" && (
        <SalesByLesson dateDebut={fields.dateDebut.value} dateFin={fields.dateFin.value} />
      )}
    </Card>
  );
}

export default Sales;
